import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Breadcrumbs,
	Link,
	CircularProgress,
	LinearProgress,
	Select,
	MenuItem,
	TextField,
	InputAdornment,
	IconButton,
	Chip,
	Stack,
	Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { supabase } from "../../config/supabase";
import { roleUtils } from "../../utils/roleUtils";
import SendNotificationModal from "../../components/notifications/SendNotificationModal";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";

const FunmiyoReport = () => {
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();

	// State variables for each stage
	const [currentStage, setCurrentStage] = useState(1);
	const [admins, setAdmins] = useState([]);
	const [selectedAdmin, setSelectedAdmin] = useState(null);
	const [adminSchools, setAdminSchools] = useState([]);
	const [selectedSchool, setSelectedSchool] = useState(null);
	const [schoolDetails, setSchoolDetails] = useState(null);
	const [selectedTeacher, setSelectedTeacher] = useState(null);
	const [teacherDetails, setTeacherDetails] = useState(null);
	const [loading, setLoading] = useState(false);

	const [roleFilter, setRoleFilter] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");

	// Notification modal states
	const [openNotificationModal, setOpenNotificationModal] = useState(false);
	const [selectedUserForNotification, setSelectedUserForNotification] =
		useState(null);

	// Get current user from auth context
	const { user } = useAuth();
	const [currentUserProfile, setCurrentUserProfile] = useState(null);

	// Compact table styles
	const compactTableStyles = {
		"& .MuiTableCell-root": {
			padding: "8px 12px",
			fontSize: "0.875rem",
		},
		"& .MuiTableCell-head": {
			fontWeight: 600,
			backgroundColor: "#f5f5f5",
			fontSize: "0.875rem",
		},
		"& .MuiTableRow-root:hover": {
			backgroundColor: "#f9f9f9",
		},
	};

	const getRoleOptions = () => {
		const uniqueRoles = [...new Set(admins.map((admin) => admin.role))];
		return ["all", ...uniqueRoles];
	};

	const getFilteredAdmins = () => {
		// First filter by role
		let filtered = admins;
		if (roleFilter !== "all") {
			filtered = filtered.filter((admin) => admin.role === roleFilter);
		}

		// Then filter by search term if there is one
		if (searchTerm.trim() !== "") {
			const searchLower = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(admin) =>
					admin.full_name?.toLowerCase().includes(searchLower) ||
					admin.email?.toLowerCase().includes(searchLower)
			);
		}

		return filtered;
	};

	// Helper function to format role names
	const formatRole = (role) => {
		return role
			.replace("ROLE_", "")
			.replace(/_/g, " ")
			.toLowerCase()
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	// Helper function to get role color
	const getRoleColor = (role) => {
		const roleColors = {
			ROLE_SCHOOL_TEACHER: "primary",
			ROLE_ADMIN: "secondary",
			ROLE_SUPER_ADMIN: "error",
			ROLE_MANAGER: "warning",
		};
		return roleColors[role] || "default";
	};

	useEffect(() => {
		fetchAdminUsers();
		fetchCurrentUserProfile();
	}, []);

	// Fetch current user profile for sending notifications
	const fetchCurrentUserProfile = async () => {
		if (!user?.id) return;

		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("id, role, status, full_name, email")
				.eq("id", user.id)
				.single();

			if (error) throw error;
			setCurrentUserProfile(data);
		} catch (error) {
			console.error("Error fetching current user profile:", error);
		}
	};

	// Helper function to get onboarding progress for teachers
	const getOnboardingProgress = async (teacherId) => {
		try {
			// Get total onboarding videos count
			const { count: totalVideos, error: videosError } = await supabase
				.from("onboarding_videos")
				.select("id", { count: "exact" });

			if (videosError) throw videosError;

			// Get completed onboarding videos count for this teacher
			const { count: completedVideos, error: progressError } = await supabase
				.from("teacher_onboarding_progress")
				.select("id", { count: "exact" })
				.eq("teacher_id", teacherId)
				.eq("status", "completed");

			if (progressError) throw progressError;

			return {
				completed: completedVideos || 0,
				total: totalVideos || 0,
			};
		} catch (error) {
			console.error("Error fetching onboarding progress:", error);
			return { completed: 0, total: 0 };
		}
	};

	const fetchAdminUsers = async () => {
		setLoading(true);
		try {
			// Fetch users with school information for teachers
			const { data, error } = await supabase.from("profiles").select(`
				id, 
				full_name, 
				email, 
				role,
				school_id,
				schools:school_id (
					name
				)
			`);

			if (error) throw error;

			// For each admin, count their allocated schools and onboarding progress for teachers
			const adminsWithDetails = await Promise.all(
				data.map(async (admin) => {
					const { count, error: countError } = await supabase
						.from("employee_schools")
						.select("id", { count: "exact" })
						.eq("profile_id", admin.id);

					if (countError) throw countError;

					// Get onboarding progress for teachers
					let onboardingProgress = null;
					if (admin.role === "ROLE_SCHOOL_TEACHER") {
						onboardingProgress = await getOnboardingProgress(admin.id);
					}

					return {
						...admin,
						totalSchools: count || 0,
						schoolName: admin.schools?.name || null,
						onboardingProgress,
					};
				})
			);

			setAdmins(adminsWithDetails);
		} catch (error) {
			console.error("Error fetching admin users:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchAdminSchools = async (adminId) => {
		setLoading(true);
		try {
			// Find schools allocated to this admin
			const { data: employeeSchools, error: employeeSchoolsError } =
				await supabase
					.from("employee_schools")
					.select("school_id")
					.eq("profile_id", adminId);

			if (employeeSchoolsError) throw employeeSchoolsError;

			if (employeeSchools.length === 0) {
				setAdminSchools([]);
				setLoading(false);
				return;
			}

			const schoolIds = employeeSchools.map((es) => es.school_id);

			// Get school details
			const { data: schools, error: schoolsError } = await supabase
				.from("schools")
				.select("id, name, principal_name, principal_contact")
				.in("id", schoolIds);

			if (schoolsError) throw schoolsError;

			// Get grades and teachers for each school
			const schoolsWithDetails = await Promise.all(
				schools.map(async (school) => {
					// Get grades for this school
					const { data: schoolGrades, error: gradesError } = await supabase
						.from("school_grades")
						.select("grade_id")
						.eq("school_id", school.id);

					if (gradesError) throw gradesError;

					let grades = [];
					if (schoolGrades.length > 0) {
						const gradeIds = schoolGrades.map((sg) => sg.grade_id);
						const { data: gradeNames, error: gradeNamesError } = await supabase
							.from("grades")
							.select("id, name")
							.in("id", gradeIds);

						if (gradeNamesError) throw gradeNamesError;
						grades = gradeNames.map((g) => g.name);
					}

					// Count teachers for this school
					const { data: teachers, error: teachersError } = await supabase
						.from("profiles")
						.select("id")
						.eq("school_id", school.id)
						.eq("role", "ROLE_SCHOOL_TEACHER");

					if (teachersError) throw teachersError;

					return {
						...school,
						grades,
						totalTeachers: teachers ? teachers.length : 0,
					};
				})
			);

			setAdminSchools(schoolsWithDetails);
		} catch (error) {
			console.error("Error fetching admin schools:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchSchoolDetails = async (schoolId) => {
		setLoading(true);
		try {
			// Get school basic info
			const { data: school, error: schoolError } = await supabase
				.from("schools")
				.select("id, name")
				.eq("id", schoolId)
				.single();

			if (schoolError) throw schoolError;

			// Get school grades
			const { data: schoolGrades, error: gradesError } = await supabase
				.from("school_grades")
				.select("grade_id")
				.eq("school_id", schoolId);

			if (gradesError) throw gradesError;

			let grades = [];
			if (schoolGrades.length > 0) {
				const gradeIds = schoolGrades.map((sg) => sg.grade_id);
				const { data: gradeNames, error: gradeNamesError } = await supabase
					.from("grades")
					.select("id, name")
					.in("id", gradeIds);

				if (gradeNamesError) throw gradeNamesError;
				grades = gradeNames;
			}

			// Get teachers in this school
			const { data: teachers, error: teachersError } = await supabase
				.from("profiles")
				.select("id, full_name, email")
				.eq("school_id", schoolId)
				.eq("role", "ROLE_SCHOOL_TEACHER");

			if (teachersError) throw teachersError;

			// For each teacher, get their grades and course completion info
			const teachersWithDetails = await Promise.all(
				teachers.map(async (teacher) => {
					// Get teacher grades
					const { data: teacherGrades, error: tGradesError } = await supabase
						.from("teacher_grades")
						.select("grade_id")
						.eq("profile_id", teacher.id)
						.eq("school_id", schoolId);

					if (tGradesError) throw tGradesError;

					let teachingGrades = [];
					if (teacherGrades.length > 0) {
						const tGradeIds = teacherGrades.map((tg) => tg.grade_id);
						const { data: tGradeNames, error: tGradeNamesError } =
							await supabase.from("grades").select("name").in("id", tGradeIds);

						if (tGradeNamesError) throw tGradeNamesError;
						teachingGrades = tGradeNames.map((g) => g.name);
					}

					// Count completed courses
					const { data: completedCourses, error: coursesError } = await supabase
						.from("teacher_course_progress")
						.select("id")
						.eq("teacher_id", teacher.id)
						.eq("status", "completed")
						.eq("academic_year", selectedAcademicYear);

					if (coursesError) throw coursesError;

					return {
						id: teacher.id,
						name: teacher.full_name,
						email: teacher.email,
						grades: teachingGrades,
						coursesCompleted: completedCourses ? completedCourses.length : 0,
					};
				})
			);

			setSchoolDetails({
				id: school.id,
				name: school.name,
				grades: grades.map((g) => g.name),
				teachers: teachersWithDetails,
			});
		} catch (error) {
			console.error("Error fetching school details:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchTeacherDetails = async (teacherId) => {
		setLoading(true);
		try {
			// Get teacher profile info
			const { data: teacher, error: teacherError } = await supabase
				.from("profiles")
				.select("id, full_name, email, phone_number, school_id")
				.eq("id", teacherId)
				.single();

			if (teacherError) throw teacherError;

			// Get teacher grades
			const { data: teacherGrades, error: gradesError } = await supabase
				.from("teacher_grades")
				.select("grade_id")
				.eq("profile_id", teacherId);

			if (gradesError) throw gradesError;

			let grades = [];
			if (teacherGrades.length > 0) {
				const gradeIds = teacherGrades.map((tg) => tg.grade_id);
				const { data: gradeNames, error: gradeNamesError } = await supabase
					.from("grades")
					.select("name")
					.in("id", gradeIds);

				if (gradeNamesError) throw gradeNamesError;
				grades = gradeNames.map((g) => g.name);
			}

			// Fetch all courses progress for this teacher from teacher_course_progress
			const { data: coursesProgress, error: coursesProgressError } =
				await supabase
					.from("teacher_course_progress")
					.select(`*`)
					.eq("teacher_id", teacherId)
					.eq("academic_year", selectedAcademicYear);

			// If teacher_id doesn't work, try with profile_id
			let teacherCoursesProgress = coursesProgress;
			if (coursesProgressError) {
				console.error("Error fetching with teacher_id:", coursesProgressError);

				const { data: altCoursesProgress, error: altError } = await supabase
					.from("teacher_course_progress")
					.select(
						`
						id, 
						category_id, 
						grade_id,
						started_at, 
						last_accessed_at,
						status,
						completion_percentage,
						created_at,
						updated_at
					`
					)
					.eq("profile_id", teacherId)
					.eq("academic_year", selectedAcademicYear);

				if (altError) {
					console.error("Error fetching with profile_id:", altError);
				} else {
					teacherCoursesProgress = altCoursesProgress;
				}
			}


			if (!teacherCoursesProgress || teacherCoursesProgress.length === 0) {
			}

			// Separate courses by status
			const inProgressCourses =
				teacherCoursesProgress?.filter(
					(course) =>
						course.status === "in_progress" ||
						(course.completion_percentage > 0 &&
							course.completion_percentage < 100)
				) || [];

			const completedCourses =
				teacherCoursesProgress?.filter(
					(course) =>
						course.status === "completed" ||
						course.completion_percentage === 100
				) || [];

			const notStartedCourses =
				teacherCoursesProgress?.filter(
					(course) =>
						course.status === "not_started" ||
						course.completion_percentage === 0
				) || [];

			// Fetch course categories details for proper display
			const inProgressWithDetails = await Promise.all(
				inProgressCourses.map(async (course) => {
					let categoryName = "Unknown";

					// Get category name if category_id exists
					if (course.category_id) {
						const { data: category, error: categoryError } = await supabase
							.from("course_categories")
							.select("name, description")
							.eq("id", course.category_id)
							.single();

						if (!categoryError && category) {
							categoryName = category.name;
						} else {
							console.error("Category fetch error:", categoryError);
						}
					}

					// Get grade name if grade_id exists
					let gradeName = "Unknown";
					if (course.grade_id) {
						const { data: grade, error: gradeError } = await supabase
							.from("grades")
							.select("name")
							.eq("id", course.grade_id)
							.single();

						if (!gradeError && grade) {
							gradeName = grade.name;
						}
					}

					return {
						id: course.id,
						categoryName,
						gradeName,
						started_at: course.started_at,
						last_accessed_at: course.last_accessed_at,
						completion_percentage: course.completion_percentage || 0,
					};
				})
			);

			const completedWithDetails = await Promise.all(
				completedCourses.map(async (course) => {
					let categoryName = "Unknown";

					// Get category name if category_id exists
					if (course.category_id) {
						const { data: category, error: categoryError } = await supabase
							.from("course_categories")
							.select("name, description")
							.eq("id", course.category_id)
							.single();

						if (!categoryError && category) {
							categoryName = category.name;
						} else {
							console.error("Category fetch error:", categoryError);
						}
					}

					// Get grade name if grade_id exists
					let gradeName = "Unknown";
					if (course.grade_id) {
						const { data: grade, error: gradeError } = await supabase
							.from("grades")
							.select("name")
							.eq("id", course.grade_id)
							.single();

						if (!gradeError && grade) {
							gradeName = grade.name;
						}
					}

					return {
						id: course.id,
						categoryName,
						gradeName,
						started_at: course.started_at,
						completed_at: course.last_accessed_at, // Using last_accessed as completion date for completed courses
						last_accessed_at: course.last_accessed_at,
					};
				})
			);

			// Get scheduled courses from teacher_schedules
			const { data: scheduledCourses, error: scheduledError } = await supabase
				.from("teacher_schedules")
				.select(
					"id, day_of_week, period, start_date, end_date, status, recurring_pattern"
				)
				.eq("teacher_id", teacherId);

			let teacherSchedules = scheduledCourses;
			if (scheduledError) {
				console.error(
					"Error fetching schedules with teacher_id:",
					scheduledError
				);

				const { data: altSchedules, error: altError } = await supabase
					.from("teacher_schedules")
					.select(
						"id, day_of_week, period, start_date, end_date, status, recurring_pattern"
					)
					.eq("profile_id", teacherId);

				if (altError) {
					console.error("Error fetching schedules with profile_id:", altError);
				} else {
					teacherSchedules = altSchedules;
				}
			}

			

			setTeacherDetails({
				id: teacher.id,
				name: teacher.full_name,
				email: teacher.email,
				contact: teacher.phone_number || "Not available",
				grades: grades,
				inProgressCourses: inProgressWithDetails || [],
				completedCourses: completedWithDetails || [],
				scheduledCourses: teacherSchedules || [],
			});
		} catch (error) {
			console.error("Error fetching teacher details:", error);
		} finally {
			setLoading(false);
		}
	};

	// Handler functions for navigation between stages
	const handleViewAdmin = (admin) => {
		setSelectedAdmin(admin);
		fetchAdminSchools(admin.id);
		setCurrentStage(2);
	};

	// Updated handler for sending notifications
	const handleSendNotification = (user) => {
		setSelectedUserForNotification(user);
		setOpenNotificationModal(true);
	};

	const handleViewSchool = (school) => {
		setSelectedSchool(school);
		fetchSchoolDetails(school.id);
		setCurrentStage(3);
	};

	const handleViewTeacher = (teacher) => {
		setSelectedTeacher(teacher);
		fetchTeacherDetails(teacher.id);
		setCurrentStage(4);
	};

	const handleBack = () => {
		if (currentStage === 2) {
			setCurrentStage(1);
			setSelectedAdmin(null);
		} else if (currentStage === 3) {
			setCurrentStage(2);
			setSelectedSchool(null);
			setSchoolDetails(null);
		} else if (currentStage === 4) {
			setCurrentStage(3);
			setSelectedTeacher(null);
			setTeacherDetails(null);
		}
	};

	// Render Stage 1: Admin users list
	const renderAdminList = () => (
		<Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Users ({getFilteredAdmins().length})
				</Typography>
				<Stack direction="row" spacing={2} alignItems="center">
					<TextField
						placeholder="Search by name or email"
						variant="outlined"
						size="small"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						sx={{ width: "280px" }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon sx={{ fontSize: "1.2rem" }} />
								</InputAdornment>
							),
							endAdornment: searchTerm && (
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => setSearchTerm("")}
										edge="end">
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Select
						value={roleFilter}
						onChange={(e) => setRoleFilter(e.target.value)}
						size="small"
						sx={{ minWidth: 150 }}>
						{getRoleOptions().map((role) => (
							<MenuItem key={role} value={role}>
								{role === "all" ? "All Roles" : formatRole(role)}
							</MenuItem>
						))}
					</Select>
				</Stack>
			</Box>

			<TableContainer component={Paper} elevation={1}>
				<Table size="small" sx={compactTableStyles}>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Email</TableCell>
							<TableCell>Role</TableCell>
							<TableCell>School</TableCell>
							<TableCell align="center">Onboarding Videos</TableCell>
							<TableCell align="center">Schools</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{getFilteredAdmins().map((admin) => (
							<TableRow key={admin.id}>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{admin.full_name}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{admin.email}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip
										label={formatRole(admin.role)}
										color={getRoleColor(admin.role)}
										size="small"
										variant="outlined"
									/>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{admin.role === "ROLE_SCHOOL_TEACHER"
											? admin.schoolName || "Not assigned"
											: "-"}
									</Typography>
								</TableCell>
								<TableCell align="center">
									{admin.role === "ROLE_SCHOOL_TEACHER" &&
									admin.onboardingProgress ? (
										<Chip
											label={`${admin.onboardingProgress.completed}/${admin.onboardingProgress.total}`}
											size="small"
											color={
												admin.onboardingProgress.completed ===
													admin.onboardingProgress.total &&
												admin.onboardingProgress.total > 0
													? "success"
													: admin.onboardingProgress.completed > 0
													? "warning"
													: "default"
											}
											variant="outlined"
										/>
									) : (
										<Typography variant="body2" color="text.secondary">
											-
										</Typography>
									)}
								</TableCell>
								<TableCell align="center">
									<Chip
										label={admin.totalSchools}
										size="small"
										color={admin.totalSchools > 0 ? "success" : "default"}
										variant="outlined"
									/>
								</TableCell>
								<TableCell align="center">
									<Stack direction="row" spacing={1} justifyContent="center">
										<Tooltip title="Send Notification">
											<IconButton
												onClick={() => handleSendNotification(admin)}
												size="small"
												color="primary">
												<NotificationsIcon fontSize="small" />
											</IconButton>
										</Tooltip>
										<Tooltip title="View Details">
											<IconButton
												onClick={() => handleViewAdmin(admin)}
												size="small"
												color="primary">
												<VisibilityIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Stack>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);

	// Handler function for sending school-wide notifications
	const handleSendSchoolNotification = (school) => {
		// Create a school object with the required structure for the modal
		const schoolForNotification = {
			id: school.id,
			name: school.name,
			recipientType: "school",
			schoolId: school.id,
		};
		setSelectedUserForNotification(schoolForNotification);
		setOpenNotificationModal(true);
	};

	// Render Stage 2: Schools allocated to selected admin
	const renderAdminSchools = () => (
		<Box>
			<Box mb={3}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={handleBack}
					variant="outlined"
					sx={{ mb: 2 }}>
					Back to Users
				</Button>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Schools - {selectedAdmin?.full_name}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{adminSchools.length} school(s) allocated
				</Typography>
			</Box>

			<TableContainer component={Paper} elevation={1}>
				<Table size="small" sx={compactTableStyles}>
					<TableHead>
						<TableRow>
							<TableCell>School Name</TableCell>
							<TableCell>Grades</TableCell>
							<TableCell align="center">Teachers</TableCell>
							<TableCell>Principal</TableCell>
							<TableCell>Contact</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{adminSchools.map((school) => (
							<TableRow key={school.id}>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{school.name}
									</Typography>
								</TableCell>
								<TableCell>
									<Stack direction="row" spacing={0.5} flexWrap="wrap">
										{school.grades.length > 0 ? (
											school.grades
												.slice(0, 3)
												.map((grade, index) => (
													<Chip
														key={index}
														label={grade}
														size="small"
														variant="outlined"
														color="primary"
													/>
												))
										) : (
											<Typography variant="body2" color="text.secondary">
												None
											</Typography>
										)}
										{school.grades.length > 3 && (
											<Chip
												label={`+${school.grades.length - 3}`}
												size="small"
												variant="outlined"
												color="default"
											/>
										)}
									</Stack>
								</TableCell>
								<TableCell align="center">
									<Chip
										label={school.totalTeachers}
										size="small"
										color={school.totalTeachers > 0 ? "success" : "default"}
										variant="outlined"
									/>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{school.principal_name || "Not available"}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{school.principal_contact || "Not available"}
									</Typography>
								</TableCell>
								<TableCell align="center">
									<Stack direction="row" spacing={1} justifyContent="center">
										<Tooltip title="Notify All Teachers in School">
											<IconButton
												onClick={() => handleSendSchoolNotification(school)}
												size="small"
												color="primary"
												disabled={school.totalTeachers === 0}>
												<NotificationsIcon fontSize="small" />
											</IconButton>
										</Tooltip>
										<Tooltip title="View School Details">
											<IconButton
												onClick={() => handleViewSchool(school)}
												size="small"
												color="primary">
												<VisibilityIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Stack>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);

	// Render Stage 3: School details with teachers
	const renderSchoolDetails = () => (
		<Box>
			<Box mb={3}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={handleBack}
					variant="outlined"
					sx={{ mb: 2 }}>
					Back to Schools
				</Button>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					{schoolDetails?.name}
				</Typography>
				<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						Grades:
					</Typography>
					{schoolDetails?.grades.length > 0 ? (
						schoolDetails.grades.map((grade, index) => (
							<Chip
								key={index}
								label={grade}
								size="small"
								variant="outlined"
								color="primary"
							/>
						))
					) : (
						<Typography variant="body2" color="text.secondary">
							None
						</Typography>
					)}
				</Stack>
			</Box>

			<TableContainer component={Paper} elevation={1}>
				<Table size="small" sx={compactTableStyles}>
					<TableHead>
						<TableRow>
							<TableCell>Teacher Name</TableCell>
							<TableCell>Teaching Grades</TableCell>
							<TableCell align="center">Courses Completed</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{schoolDetails?.teachers.map((teacher) => (
							<TableRow key={teacher.id}>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{teacher.name}
									</Typography>
								</TableCell>
								<TableCell>
									<Stack direction="row" spacing={0.5} flexWrap="wrap">
										{teacher.grades.length > 0 ? (
											teacher.grades.map((grade, index) => (
												<Chip
													key={index}
													label={grade}
													size="small"
													variant="outlined"
													color="secondary"
												/>
											))
										) : (
											<Typography variant="body2" color="text.secondary">
												None
											</Typography>
										)}
									</Stack>
								</TableCell>
								<TableCell align="center">
									<Chip
										label={teacher.coursesCompleted}
										size="small"
										color={teacher.coursesCompleted > 0 ? "success" : "default"}
										variant="outlined"
									/>
								</TableCell>
								<TableCell align="center">
									<Stack direction="row" spacing={1} justifyContent="center">
										<Tooltip title="Send Notification">
											<IconButton
												onClick={() => handleSendNotification(teacher)}
												size="small"
												color="primary">
												<NotificationsIcon fontSize="small" />
											</IconButton>
										</Tooltip>
										<Tooltip title="View Teacher Details">
											<IconButton
												onClick={() => handleViewTeacher(teacher)}
												size="small"
												color="primary">
												<VisibilityIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Stack>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);

	// Render Stage 4: Teacher details
	const renderTeacherDetails = () => (
		<Box>
			<Box mb={3}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={handleBack}
					variant="outlined"
					sx={{ mb: 2 }}>
					Back to Teachers
				</Button>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					{teacherDetails?.name}
				</Typography>
				<Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
					<Stack direction="row" spacing={4} flexWrap="wrap">
						<Box>
							<Typography variant="body2" color="text.secondary">
								Email
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{teacherDetails?.email}
							</Typography>
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary">
								Contact
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{teacherDetails?.contact}
							</Typography>
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary">
								Teaching Grades
							</Typography>
							<Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
								{teacherDetails?.grades.length > 0 ? (
									teacherDetails.grades.map((grade, index) => (
										<Chip
											key={index}
											label={grade}
											size="small"
											variant="outlined"
											color="primary"
										/>
									))
								) : (
									<Typography variant="body2" color="text.secondary">
										None
									</Typography>
								)}
							</Stack>
						</Box>
					</Stack>
				</Box>
			</Box>

			{/* SECTION 1: COURSES IN PROGRESS */}
			<Box mb={4}>
				<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
					Courses In Progress ({teacherDetails?.inProgressCourses.length || 0})
				</Typography>
				<TableContainer component={Paper} elevation={1}>
					<Table size="small" sx={compactTableStyles}>
						<TableHead>
							<TableRow>
								<TableCell>Course Category</TableCell>
								<TableCell>Grade</TableCell>
								<TableCell>Started</TableCell>
								<TableCell>Last Accessed</TableCell>
								<TableCell align="center">Progress</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{teacherDetails?.inProgressCourses.length > 0 ? (
								teacherDetails.inProgressCourses.map((course) => (
									<TableRow key={course.id}>
										<TableCell>
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{course.categoryName}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={course.gradeName}
												size="small"
												variant="outlined"
												color="primary"
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{course.started_at
													? new Date(course.started_at).toLocaleDateString(
															"en-US",
															{
																month: "short",
																day: "numeric",
																year: "numeric",
															}
													  )
													: "N/A"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{course.last_accessed_at
													? new Date(
															course.last_accessed_at
													  ).toLocaleDateString("en-US", {
															month: "short",
															day: "numeric",
															year: "numeric",
													  })
													: "N/A"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<LinearProgress
													variant="determinate"
													value={course.completion_percentage}
													sx={{
														width: 60,
														height: 6,
														borderRadius: 3,
														backgroundColor: "#e0e0e0",
													}}
												/>
												<Typography variant="body2" color="text.secondary">
													{course.completion_percentage}%
												</Typography>
											</Box>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ py: 2 }}>
											No courses in progress
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			{/* SECTION 2: COMPLETED COURSES */}
			<Box mb={4}>
				<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
					Completed Courses ({teacherDetails?.completedCourses.length || 0})
				</Typography>
				<TableContainer component={Paper} elevation={1}>
					<Table size="small" sx={compactTableStyles}>
						<TableHead>
							<TableRow>
								<TableCell>Course Category</TableCell>
								<TableCell>Grade</TableCell>
								<TableCell>Started</TableCell>
								<TableCell>Completed</TableCell>
								<TableCell align="center">Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{teacherDetails?.completedCourses.length > 0 ? (
								teacherDetails.completedCourses.map((course) => (
									<TableRow key={course.id}>
										<TableCell>
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{course.categoryName}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={course.gradeName}
												size="small"
												variant="outlined"
												color="primary"
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{course.started_at
													? new Date(course.started_at).toLocaleDateString(
															"en-US",
															{
																month: "short",
																day: "numeric",
																year: "numeric",
															}
													  )
													: "N/A"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{course.completed_at
													? new Date(course.completed_at).toLocaleDateString(
															"en-US",
															{
																month: "short",
																day: "numeric",
																year: "numeric",
															}
													  )
													: "N/A"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Chip
												label="Completed"
												size="small"
												color="success"
												variant="outlined"
											/>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ py: 2 }}>
											No completed courses
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			{/* SECTION 3: SCHEDULED COURSES */}
			<Box>
				<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
					Scheduled Courses ({teacherDetails?.scheduledCourses.length || 0})
				</Typography>
				<TableContainer component={Paper} elevation={1}>
					<Table size="small" sx={compactTableStyles}>
						<TableHead>
							<TableRow>
								<TableCell>Day</TableCell>
								<TableCell align="center">Period</TableCell>
								<TableCell>Start Date</TableCell>
								<TableCell>End Date</TableCell>
								<TableCell align="center">Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{teacherDetails?.scheduledCourses.length > 0 ? (
								teacherDetails.scheduledCourses.map((schedule) => (
									<TableRow key={schedule.id}>
										<TableCell>
											<Chip
												label={
													[
														"Sunday",
														"Monday",
														"Tuesday",
														"Wednesday",
														"Thursday",
														"Friday",
														"Saturday",
													][schedule.day_of_week - 1]
												}
												size="small"
												variant="outlined"
												color="primary"
											/>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{schedule.period}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{new Date(schedule.start_date).toLocaleDateString(
													"en-US",
													{
														month: "short",
														day: "numeric",
														year: "numeric",
													}
												)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{schedule.end_date
													? new Date(schedule.end_date).toLocaleDateString(
															"en-US",
															{
																month: "short",
																day: "numeric",
																year: "numeric",
															}
													  )
													: "Ongoing"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Chip
												label={schedule.status}
												size="small"
												color={
													schedule.status === "active" ? "success" : "default"
												}
												variant="outlined"
											/>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ py: 2 }}>
											No courses scheduled yet
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);

	// Conditional rendering based on current stage
	const renderCurrentStage = () => {
		if (loading) {
			return (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="300px">
					<CircularProgress />
				</Box>
			);
		}

		switch (currentStage) {
			case 1:
				return renderAdminList();
			case 2:
				return renderAdminSchools();
			case 3:
				return renderSchoolDetails();
			case 4:
				return renderTeacherDetails();
			default:
				return renderAdminList();
		}
	};

	// Navigation breadcrumbs
	const renderBreadcrumbs = () => {
		return (
			<Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					color={currentStage === 1 ? "text.primary" : "inherit"}
					href="#"
					onClick={() => setCurrentStage(1)}
					underline={currentStage === 1 ? "none" : "hover"}
					sx={{ fontWeight: currentStage === 1 ? "bold" : "normal" }}>
					Users
				</Link>

				{currentStage >= 2 && (
					<Link
						color={currentStage === 2 ? "text.primary" : "inherit"}
						href="#"
						onClick={() => currentStage > 2 && setCurrentStage(2)}
						underline={currentStage === 2 ? "none" : "hover"}
						sx={{ fontWeight: currentStage === 2 ? "bold" : "normal" }}>
						{selectedAdmin?.full_name}'s Schools
					</Link>
				)}

				{currentStage >= 3 && (
					<Link
						color={currentStage === 3 ? "text.primary" : "inherit"}
						href="#"
						onClick={() => currentStage > 3 && setCurrentStage(3)}
						underline={currentStage === 3 ? "none" : "hover"}
						sx={{ fontWeight: currentStage === 3 ? "bold" : "normal" }}>
						{selectedSchool?.name}
					</Link>
				)}

				{currentStage >= 4 && (
					<Typography color="text.primary" sx={{ fontWeight: "bold" }}>
						{selectedTeacher?.name}
					</Typography>
				)}
			</Breadcrumbs>
		);
	};

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mb: 1 }}>
				<Typography
					variant="h4"
					component="h1"
					sx={{ fontWeight: 600, mb: 0 }}>
					Funmiyo Reporting System
				</Typography>
				<AcademicYearSelector
					value={selectedAcademicYear}
					onChange={setSelectedAcademicYear}
				/>
			</Stack>

			{renderBreadcrumbs()}
			{renderCurrentStage()}

			{/* Send Notification Modal */}
			{currentUserProfile && selectedUserForNotification && (
				<SendNotificationModal
					open={openNotificationModal}
					onClose={() => {
						setOpenNotificationModal(false);
						setSelectedUserForNotification(null);
					}}
					supabase={supabase}
					currentUser={currentUserProfile}
					preselectedRecipient={selectedUserForNotification}
				/>
			)}
		</Container>
	);
};

export default FunmiyoReport;
